import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Private per-browser guest ID so signed-out visitors never share demo/practice data.
const GUEST_KEY = 'icraftz_guest_id';
function guestId() {
  try {
    let g = localStorage.getItem(GUEST_KEY);
    if (!g) {
      g = (window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(GUEST_KEY, g);
    }
    return g;
  } catch (e) { return null; }
}
const _gid = guestId();
if (_gid) axios.defaults.headers.common['X-Guest-Id'] = _gid;

const TOKEN_KEY = 'icraftz_auth_token';

function readToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; }
  catch (err) { return null; }
}
function writeToken(t) {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); }
  catch { /* ignored */ }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(readToken());
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => (
    token ? { Authorization: `Bearer ${token}` } : {}
  ), [token]);

  // Hydrate /me on mount when a token exists
  useEffect(() => {
    let cancelled = false;
    async function go() {
      if (!token) { setUser(null); setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setUser(res.data);
      } catch (err) {
        if (!cancelled) {
          // Token rejected (expired/revoked) — clear and reset auth state.
          writeToken(null);
          setToken(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    go();
    return () => { cancelled = true; };
  }, [token]);

  const signup = useCallback(async (payload) => {
    // Accepts: name, email, password (required) +
    //          phone, age_range, content_prefs, payment_pref, consent (optional)
    const res = await axios.post(`${API}/auth/signup`, payload);
    return res.data; // returns the user (unverified)
  }, []);

  const login = useCallback(async ({ email, password, remember_me = false }) => {
    const res = await axios.post(`${API}/auth/login`, { email, password, remember_me });
    const { token: t, user: u } = res.data;
    writeToken(t);
    setToken(t);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    const t = readToken();
    if (t) axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${t}` } }).catch(() => {});
    writeToken(null);
    setToken(null);
    setUser(null);
  }, []);

  const verifyEmail = useCallback(async (verifyToken) => {
    const res = await axios.post(`${API}/auth/verify-email`, { token: verifyToken });
    return res.data;
  }, []);

  const resendVerification = useCallback(async (email) => {
    const res = await axios.post(`${API}/auth/resend-verification`, { email });
    return res.data;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    const res = await axios.post(`${API}/auth/forgot-password`, { email });
    return res.data;
  }, []);

  const resetPassword = useCallback(async (resetToken, password) => {
    const res = await axios.post(`${API}/auth/reset-password`, { token: resetToken, password });
    return res.data;
  }, []);

  const value = {
    token,
    user,
    loading,
    isAuthenticated: !!user,
    authHeaders,
    signup,
    login,
    logout,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
